import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
} from 'react-native';
import { useNavigation, useRoute } from '@react-navigation/native';
import { useDesktopColors } from '@/constants/residents/desktop/color';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { LoadingSpinner } from '@/components';
import { WebConfirmModal } from '@/components/common/modals';
import { UnifiedModal, ModalButton } from '@/components/common/UnifiedModal';
import { useToast } from '@/contexts/admin/ToastContext';
import { extractErrorMessage } from '@/utils/common/errorHandling';
import { PaymentMethodCard } from '@/components/residents/payments';
import { usePaymentData } from '@/features/payments/hooks/usePaymentData';
import { usePaymentMethodSelection } from '@/features/payments/hooks/usePaymentValidation';
import { 
  getPaymentMethodLabel, 
  hasPaymentMethodCardExpired,
} from '@/utils/residents/paymentUtils';
import { 
  usePutBankAccountMutation,
  usePutCreditCardMutation,
  useDeleteBankAccountMutation,
  useDeleteCreditCardMutation,
} from '@/store/api';

type Params = {
  leaseId: number;
  residentId?: number;
  selectedMethodId?: string | number;
  returnTo?: string;
  successPage?: string;
  addOrChangePaymentMethodPage?: string;
};

export const ManageMyWalletModal: React.FC = () => {
  const navigation = useNavigation<any>();
  const route = useRoute<any>();
  const { showSuccess, showError } = useToast();
  const DESKTOP_COLORS = useDesktopColors();
  const { 
    leaseId, 
    residentId, 
    selectedMethodId, 
    returnTo, 
    successPage,
    addOrChangePaymentMethodPage 
  } = (route.params || {}) as Params;

  // Get payment data
  const {
    paymentMethods,
    selectedPaymentMethod,
    isLoading,
    updateSelectedPaymentMethod,
    refreshPaymentMethods,
  } = usePaymentData(residentId);

  // Payment method selection logic
  const methodSelection = usePaymentMethodSelection(paymentMethods, selectedMethodId);
  const {
    availableMethods,
    expiredMethods,
    blockedMethods,
    hasValidMethods,
    allMethodsExpired,
  } = methodSelection;

  // Local state
  const [localSelectedMethod, setLocalSelectedMethod] = useState(
    selectedPaymentMethod || methodSelection.selectedMethod
  );

  // API mutations
  const [putBankAccount, { isLoading: isUpdatingBank }] = usePutBankAccountMutation();
  const [putCreditCard, { isLoading: isUpdatingCard }] = usePutCreditCardMutation();
  const [deleteBankAccount, { isLoading: isDeletingBank }] = useDeleteBankAccountMutation();
  const [deleteCreditCard, { isLoading: isDeletingCard }] = useDeleteCreditCardMutation();
  
  const isUpdating = isUpdatingBank || isUpdatingCard;
  const isDeleting = isDeletingBank || isDeletingCard;

  // Delete confirmation modal state
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [methodToDelete, setMethodToDelete] = useState<any>(null);

  const hasNoMethods = paymentMethods.length === 0;
  const hasOnlyOneMethod = paymentMethods.length === 1;

  // Handle method selection
  const handleSelectMethod = (method: any) => {
    setLocalSelectedMethod(method);
  };

  // Handle save changes
  const handleSaveChanges = () => {
    if (localSelectedMethod) {
      updateSelectedPaymentMethod(localSelectedMethod);
      
      if (successPage) {
        navigation.replace(successPage, { leaseId });
      } else if (returnTo) {
        navigation.replace(returnTo, { leaseId });
      } else {
        navigation.goBack();
      }
    }
  };

  // Handle add new payment method
  const handleAddPaymentMethod = () => {
    // Replace current modal with add payment method modal
    if (addOrChangePaymentMethodPage) {
      navigation.replace(addOrChangePaymentMethodPage, { 
        leaseId,
        returnTo: 'ManageMyWallet'
      });
    } else {
      navigation.replace('AddOrChangePaymentMethod', { 
        leaseId,
        returnTo: 'ManageMyWallet'
      });
    }
  };

  // Handle set as default
  const handleSetAsDefault = async (method: any) => {
    try {
      if (method.type === 'bankAccount' || method.accttype === 'ECHK') {
        await putBankAccount({
          sourceId: method.sourceId || method.id,
          bankAccount: { isDefault: true },
        }).unwrap();
      } else {
        await putCreditCard({
          sourceId: method.sourceId || method.id,
          creditCard: { isDefault: true },
        }).unwrap();
      }
      
      // Show success toast
      showSuccess('Payment method set as default successfully');
      
      // Refresh the payment methods data (automatic via cache invalidation + manual backup)
      refreshPaymentMethods();
    } catch (error) {
      console.error('Set as default error:', error);
      showError(extractErrorMessage(error, 'Failed to set payment method as default. Please try again.'));
    }
  };

  // Handle delete payment method
  const handleDeletePaymentMethod = (method: any) => {
    setMethodToDelete(method);
    setDeleteModalVisible(true);
  };

  // Handle confirm delete
  const handleConfirmDelete = async () => {
    if (!methodToDelete) return;

    try {
      let result;
      const sourceId = methodToDelete.sourceId || methodToDelete.id;
      const isBank = methodToDelete.type === 'bankAccount' || methodToDelete.accttype === 'ECHK';
      
      if (isBank) {
        result = await deleteBankAccount({ sourceId: String(sourceId) }).unwrap();
      } else {
        result = await deleteCreditCard({ sourceId: String(sourceId) }).unwrap();
      }

      if (result.success) {
        refreshPaymentMethods();
        setDeleteModalVisible(false);
        setMethodToDelete(null);
        showSuccess('Payment method removed successfully');
      } else {
        showError('Failed to delete payment method. Please try again.');
      }
    } catch (error) {
      console.error('Delete payment method error:', error);
      showError(extractErrorMessage(error, 'Failed to delete payment method. Please try again.'));
    }
  };

  // Handle cancel delete
  const handleCancelDelete = () => {
    setDeleteModalVisible(false);
    setMethodToDelete(null);
  };

  const onClose = () => navigation.goBack();

  const hasChanges = localSelectedMethod?.id !== selectedPaymentMethod?.id;

  const renderEmptyState = () => (
    <View style={styles.emptyState}>
      <CardOutlineIcon />
      <Text style={[styles.emptyTitle, { color: DESKTOP_COLORS.TEXT_PRIMARY }]}>No saved payment methods</Text>
      <Text style={[styles.emptyMessage, { color: DESKTOP_COLORS.TEXT_TERTIARY }]}>
        You'll need to add a new payment method before making your next payment.
      </Text>
    </View>
  );

  const renderPaymentMethods = () => {
    // Combine all methods for the grid layout (matching web behavior)
    const sortedMethods = [...paymentMethods]
      .sort(({ isDefault: x = false }, { isDefault: y = false }) => Number(y) - Number(x));

    return (
      <View style={[
        styles.methodsGrid,
        hasOnlyOneMethod && styles.methodsGridSingle,
      ]}>
        {sortedMethods.map((method, index) => (
          <View
            key={method.id || method.sourceId}
            style={[styles.methodItem, hasOnlyOneMethod && styles.methodItemSingle]}
          >
            <PaymentMethodCard
              method={method}
              index={index}
              isOnlyCard={hasOnlyOneMethod}
              isExpired={hasPaymentMethodCardExpired(method.expiry)}
              onSetAsDefault={() => handleSetAsDefault(method)}
              onDelete={() => handleDeletePaymentMethod(method)}
              isUpdating={isUpdating}
              canDelete={!method.isDefault || hasOnlyOneMethod}
              hasAutoPay={false} // TODO: Check if method has autopay
              showActions={true}
            />
          </View>
        ))}
      </View>
    );
  };

  const getFooterButtons = () => {
    if (hasNoMethods) {
      return {
        backTitle: "Do it later",
        nextTitle: "Add Now",
        nextAction: handleAddPaymentMethod,
      };
    }

    return {
      backTitle: "",
      nextTitle: "Done",
      nextAction: handleSaveChanges,
    };
  };

  const footerConfig = getFooterButtons();

  const footerElement = (
    <View style={styles.footerRow}>
      {footerConfig.backTitle ? (
        <ModalButton label={footerConfig.backTitle} variant="secondary" onPress={onClose} />
      ) : null}
      <ModalButton label={footerConfig.nextTitle} variant="primary" onPress={footerConfig.nextAction} />
    </View>
  );

  return (
    <>
      <UnifiedModal
        visible={true}
        variant="dialog"
        dialogSize="large"
        onClose={onClose}
        title="Manage my wallet"
        scrollable
        footer={footerElement}
      >
        <View style={styles.modalContent}>
          {isLoading ? (
            <View style={styles.loadingContainer}>
              <LoadingSpinner />
            </View>
          ) : hasNoMethods ? (
            renderEmptyState()
          ) : (
            renderPaymentMethods()
          )}
        </View>
      </UnifiedModal>

      {/* Delete Confirmation Modal */}
      <WebConfirmModal
        visible={deleteModalVisible}
        title="Remove payment method"
        message={`Are you sure you want to remove ${methodToDelete ? getPaymentMethodLabel(methodToDelete) : 'this payment method'}?`}
        confirmText="Remove"
        cancelText="Cancel"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </>
  );
};


// Helper Icons
const CardOutlineIcon: React.FC = () => {
  const DESKTOP_COLORS = useDesktopColors();
  // Use the credit card icon as a fallback for the card outline
  const Icon = RESIDENTS_SVG_COMPONENTS.CREDIT_CARD;
  return (
    <View style={styles.emptyIconContainer}>
      <Icon width={64} height={64} color={DESKTOP_COLORS.TEXT_TERTIARY} />
    </View>
  );
};

const styles = StyleSheet.create({
  // Modal Content
  modalContent: {
    paddingTop: 8,
    minHeight: 400,
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 240,
  },

  // Empty State
  emptyState: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 48,
  },
  emptyIconContainer: {
    marginBottom: 24,
  },
  emptyTitle: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 4,
    textAlign: 'center',
  },
  emptyMessage: {
    fontSize: 12,
    textAlign: 'center',
    lineHeight: 16,
    maxWidth: 280,
  },

  // Payment Methods Grid
  methodsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  methodsGridSingle: {
    flexDirection: 'column',
  },
  methodItem: {
    width: '48%',
    marginBottom: 16,
  },
  methodItemSingle: {
    width: '100%',
  },

  // Footer
  footerRow: {
    flexDirection: 'row',
    gap: 12,
  },
});

export default ManageMyWalletModal;
